import type { EditPlanSummary, NovelDimensionScores, SocialCalibrationScores } from "./novel-dimensions";
// packages/scoring-types/src/index.ts
// FRAME — Shared scoring types across iOS, Android, and Web

// ─── Dimension Scores ────────────────────────────────────────
export interface DimensionScores {
  portraitComposition: number | null; // null = no face → landscape mode
  lighting: number;
  composition: number;
  sceneInterest: number;
  sceneInterestSpiked: number;
}

// ─── Photo Classification ───────────────────────────────────
export type PhotoCategory = 'genuine' | 'screenshot' | 'text_image' | 'meme' | 'heavily_edited';

// ─── Photo Score ─────────────────────────────────────────────
export interface PhotoScore {
  id: string;
  assetIdentifier: string;
  dimensions: DimensionScores;
  novelDimensions?: NovelDimensionScores;
  landmarkTier: LandmarkTier;
  detractorPenalty: number;
  detectedFaceCount: number;
  rawCompositeScore: number;
  compositeScore: number;
  novelCompositeScore?: number;
  blendedCompositeScore?: number;
  dimensionNotes: Record<string, string>;
  socialCalibration?: SocialCalibrationScores;
  socialCalibrationComposite?: number;
  editPlan?: EditPlanSummary;
  photoCategory?: PhotoCategory;
  classificationConfidence?: number;
  isSelected: boolean;
  processingDurationMs: number;
  thumbnailUri?: string;
  fullUri?: string;
}

// ─── Landmark Tiers ──────────────────────────────────────────
export enum LandmarkTier {
  Ordinary = 0,
  Interesting = 1,
  Notable = 2,
  Iconic = 3,
}

export const LANDMARK_COEFFICIENTS: Record<LandmarkTier, number> = {
  [LandmarkTier.Ordinary]: 1.0,
  [LandmarkTier.Interesting]: 1.1,
  [LandmarkTier.Notable]: 1.20,
  [LandmarkTier.Iconic]: 1.30,
};

export const LANDMARK_KEYWORDS: Record<LandmarkTier, string[]> = {
  [LandmarkTier.Iconic]: [
    'tower', 'pyramid', 'colosseum', 'palace', 'castle', 'taj', 'liberty',
    'sphinx', 'golden_gate', 'notre_dame', 'louvre', 'mona_lisa', 'sistine',
    'opera_house', 'kremlin', 'machu_picchu', 'stonehenge', 'petra',
  ],
  [LandmarkTier.Notable]: [
    'cathedral', 'basilica', 'mosque', 'temple', 'bridge', 'lighthouse',
    'monument', 'skyscraper', 'museum', 'gallery', 'sculpture', 'statue', 'fountain',
  ],
  [LandmarkTier.Interesting]: [
    'park', 'garden', 'plaza', 'harbor', 'waterfall', 'canyon', 'volcano',
    'glacier', 'market', 'festival', 'church', 'chapel',
  ],
  [LandmarkTier.Ordinary]: [],
};

// ─── Scoring Modes ───────────────────────────────────────────
export type ScoringModeId =
  | 'balanced'
  | 'dating'
  | 'instagram'
  | 'travel'
  | 'realEstate'
  | 'professional'
  | 'custom';

export interface WeightProfile {
  portraitWeight: number;
  lightingWeight: number;
  compositionWeight: number;
  sceneWeight: number;
  landmarkSpikeEnabled: boolean;
  landmarkSpikeStrength: number;
  detractorPenaltyEnabled: boolean;
  detractorPenaltyStrength: number;
}

export interface ScoringMode {
  id: ScoringModeId;
  label: string;
  icon: string; // SF Symbol name or icon key
  tagline: string;
  chatHint: string;
  requiresPremium: boolean;
  freePhotoLimit: number | null; // null = unlimited
  defaultWeights: WeightProfile;
}

export const SCORING_MODES: Record<ScoringModeId, ScoringMode> = {
  balanced: {
    id: 'balanced',
    label: 'Balanced',
    icon: 'scale-3d',
    tagline: 'Equal weight across all dimensions',
    chatHint: "Try: 'warm tones, natural light' or 'clean and minimal'",
    requiresPremium: false,
    freePhotoLimit: null,
    defaultWeights: {
      portraitWeight: 0.30, lightingWeight: 0.25,
      compositionWeight: 0.25, sceneWeight: 0.20,
      landmarkSpikeEnabled: false, landmarkSpikeStrength: 1.0,
      detractorPenaltyEnabled: true, detractorPenaltyStrength: 1.0,
    },
  },
  dating: {
    id: 'dating',
    label: 'Dating',
    icon: 'person-crop-circle',
    tagline: 'Find your best-composed portraits',
    chatHint: "Try: 'natural smile, soft background blur' or 'clean headshot, even lighting'",
    requiresPremium: false,
    freePhotoLimit: 10,
    defaultWeights: {
      portraitWeight: 0.40, lightingWeight: 0.25,
      compositionWeight: 0.18, sceneWeight: 0.17,
      landmarkSpikeEnabled: false, landmarkSpikeStrength: 0.5,
      detractorPenaltyEnabled: true, detractorPenaltyStrength: 1.5,
    },
  },
  instagram: {
    id: 'instagram',
    label: 'Social Post',
    icon: 'camera',
    tagline: 'Eye-catching posts with strong backdrops',
    chatHint: "Try: 'vibrant colors, eye-catching composition' or 'moody aesthetic, deep shadows'",
    requiresPremium: false,
    freePhotoLimit: 10,
    defaultWeights: {
      portraitWeight: 0.20, lightingWeight: 0.20,
      compositionWeight: 0.25, sceneWeight: 0.35,
      landmarkSpikeEnabled: true, landmarkSpikeStrength: 1.3,
      detractorPenaltyEnabled: true, detractorPenaltyStrength: 1.2,
    },
  },
  travel: {
    id: 'travel',
    label: 'Travel',
    icon: 'airplane',
    tagline: 'Epic scenes and landmark moments',
    chatHint: "Try: 'golden hour at landmarks' or 'dramatic landscapes, vivid skies'",
    requiresPremium: true,
    freePhotoLimit: null,
    defaultWeights: {
      portraitWeight: 0.15, lightingWeight: 0.20,
      compositionWeight: 0.25, sceneWeight: 0.40,
      landmarkSpikeEnabled: true, landmarkSpikeStrength: 1.5,
      detractorPenaltyEnabled: true, detractorPenaltyStrength: 1.0,
    },
  },
  realEstate: {
    id: 'realEstate',
    label: 'Real Estate',
    icon: 'house',
    tagline: 'Clean composition, no people',
    chatHint: "Try: 'bright rooms, wide angle' or 'natural light, clean lines'",
    requiresPremium: true,
    freePhotoLimit: null,
    defaultWeights: {
      portraitWeight: 0.00, lightingWeight: 0.30,
      compositionWeight: 0.40, sceneWeight: 0.30,
      landmarkSpikeEnabled: false, landmarkSpikeStrength: 0.0,
      detractorPenaltyEnabled: true, detractorPenaltyStrength: 2.0,
    },
  },
  professional: {
    id: 'professional',
    label: 'Professional',
    icon: 'briefcase',
    tagline: 'Sharp, well-lit, polished',
    chatHint: "Try: 'studio quality, sharp focus' or 'corporate headshot, neutral background'",
    requiresPremium: false,
    freePhotoLimit: 10,
    defaultWeights: {
      portraitWeight: 0.40, lightingWeight: 0.30,
      compositionWeight: 0.20, sceneWeight: 0.10,
      landmarkSpikeEnabled: false, landmarkSpikeStrength: 0.0,
      detractorPenaltyEnabled: true, detractorPenaltyStrength: 1.8,
    },
  },
  custom: {
    id: 'custom',
    label: 'Custom',
    icon: 'slider-horizontal-3',
    tagline: 'You set the weights',
    chatHint: "Describe exactly what you want — FRAME will interpret your vision",
    requiresPremium: true,
    freePhotoLimit: null,
    defaultWeights: {
      portraitWeight: 0.25, lightingWeight: 0.25,
      compositionWeight: 0.25, sceneWeight: 0.25,
      landmarkSpikeEnabled: true, landmarkSpikeStrength: 1.0,
      detractorPenaltyEnabled: true, detractorPenaltyStrength: 1.0,
    },
  },
};

// ─── Vibe Config ─────────────────────────────────────────────
export interface VibeConfig {
  rawInput: string;
  parsedLabel: string;
  portraitWeight: number;
  lightingWeight: number;
  compositionWeight: number;
  sceneWeight: number;
}

export const DEFAULT_VIBE_CONFIG: VibeConfig = {
  rawInput: '',
  parsedLabel: 'Balanced scoring',
  portraitWeight: 0.30,
  lightingWeight: 0.25,
  compositionWeight: 0.25,
  sceneWeight: 0.20,
};

// ─── Scoring Dimension Metadata ──────────────────────────────
export type DimensionKey = 'portraitComposition' | 'lighting' | 'composition' | 'sceneInterest';

export interface DimensionMeta {
  key: DimensionKey;
  label: string;
  icon: string;
  naturalLanguageLabel: string; // for weight sliders
  noteForScore: (score: number) => string;
}

export const DIMENSIONS: DimensionMeta[] = [
  {
    key: 'portraitComposition',
    label: 'Portrait Composition',
    icon: 'person-crop-circle',
    naturalLanguageLabel: 'How much does the subject matter?',
    noteForScore: (s) =>
      s >= 8 ? 'Natural, well-composed expression' :
      s >= 6 ? 'Decent expression and angle' :
      s >= 3 ? 'Expression could be better' :
      'Awkward angle or expression',
  },
  {
    key: 'lighting',
    label: 'Lighting',
    icon: 'sun-max',
    naturalLanguageLabel: 'How important is good light?',
    noteForScore: (s) =>
      s >= 8 ? 'Beautiful natural light' :
      s >= 6 ? 'Good, even lighting' :
      s >= 3 ? 'Lighting is acceptable' :
      'Harsh or flat lighting',
  },
  {
    key: 'composition',
    label: 'Composition',
    icon: 'square-grid-3x3',
    naturalLanguageLabel: 'How important is framing?',
    noteForScore: (s) =>
      s >= 8 ? 'Strong, intentional framing' :
      s >= 6 ? 'Solid composition' :
      s >= 3 ? 'Framing could be tighter' :
      'Cluttered or unbalanced frame',
  },
  {
    key: 'sceneInterest',
    label: 'Scene Interest',
    icon: 'sparkles',
    naturalLanguageLabel: 'How much does the backdrop matter?',
    noteForScore: (s) =>
      s >= 8 ? 'Visually striking scene' :
      s >= 6 ? 'Interesting setting' :
      s >= 3 ? 'Ordinary scene' :
      'Low visual interest',
  },
];

// ─── Subscription Tiers ──────────────────────────────────────
export enum SubscriptionTier {
  Free = 'free',
  Silver = 'silver',
  Gold = 'gold',
}

export interface TierEntitlements {
  tier: SubscriptionTier;
  maxDownloadsPerMonth: number;
  watermarked: boolean;
  editIntensities: EditIntensity[];
  compositeEnabled: boolean;
  customModeEnabled: boolean;
  photosPerSession: Record<ScoringModeId, number>;
}

export type EditIntensity = 'subtle' | 'lowkey' | 'balanced' | 'highkey';

export const TIER_ENTITLEMENTS: Record<SubscriptionTier, TierEntitlements> = {
  [SubscriptionTier.Free]: {
    tier: SubscriptionTier.Free,
    maxDownloadsPerMonth: 5,
    watermarked: true,
    editIntensities: ['lowkey'],
    compositeEnabled: false,
    customModeEnabled: false,
    photosPerSession: {
      balanced: Infinity,
      dating: 10,
      instagram: 10,
      professional: 10,
      travel: 0,
      realEstate: 0,
      custom: 0,
    },
  },
  [SubscriptionTier.Silver]: {
    tier: SubscriptionTier.Silver,
    maxDownloadsPerMonth: 20,
    watermarked: false,
    editIntensities: ['subtle', 'lowkey'],
    compositeEnabled: false,
    customModeEnabled: true,
    photosPerSession: {
      balanced: Infinity,
      dating: 50,
      instagram: 50,
      professional: 50,
      travel: 50,
      realEstate: 50,
      custom: 50,
    },
  },
  [SubscriptionTier.Gold]: {
    tier: SubscriptionTier.Gold,
    maxDownloadsPerMonth: Infinity,
    watermarked: false,
    editIntensities: ['subtle', 'lowkey', 'balanced', 'highkey'],
    compositeEnabled: true,
    customModeEnabled: true,
    photosPerSession: {
      balanced: Infinity,
      dating: Infinity,
      instagram: Infinity,
      professional: Infinity,
      travel: Infinity,
      realEstate: Infinity,
      custom: Infinity,
    },
  },
};

// ─── Scoring Session ─────────────────────────────────────────
export interface ScoringSession {
  id: string;
  mode: ScoringModeId;
  vibeText: string;
  photoCount: number;
  requestedCount: number;
  scores: PhotoScore[];
  thresholdScore: number;
  createdAt: string;
  weightProfile: WeightProfile;
}

// ─── Vibe Archetype (for parser) ─────────────────────────────
export interface VibeArchetype {
  name: string;
  keywords: string[];
  portraitDelta: number;
  lightingDelta: number;
  compositionDelta: number;
  sceneDelta: number;
  humanLabel: string;
}

export const VIBE_ARCHETYPES: VibeArchetype[] = [
  {
    name: 'candid',
    keywords: ['candid', 'authentic', 'natural', 'real', 'unposed', 'spontaneous'],
    portraitDelta: -0.08, lightingDelta: -0.04, compositionDelta: 0.00, sceneDelta: 0.12,
    humanLabel: 'Prioritizing authenticity and scene interest',
  },
  {
    name: 'professional',
    keywords: ['professional', 'formal', 'headshot', 'corporate', 'portrait', 'linkedin', 'polished'],
    portraitDelta: 0.15, lightingDelta: 0.05, compositionDelta: 0.00, sceneDelta: -0.20,
    humanLabel: 'Prioritizing portrait composition and lighting',
  },
  {
    name: 'travel',
    keywords: ['travel', 'adventure', 'landscape', 'explore', 'trip', 'vacation', 'journey', 'scenery'],
    portraitDelta: -0.10, lightingDelta: 0.02, compositionDelta: 0.04, sceneDelta: 0.04,
    humanLabel: 'Prioritizing scene interest for travel memories',
  },
  {
    name: 'golden_hour',
    keywords: ['golden', 'hour', 'warm', 'sunset', 'sunrise', 'glow', 'magic hour', 'dusk', 'dawn'],
    portraitDelta: -0.05, lightingDelta: 0.15, compositionDelta: -0.02, sceneDelta: -0.08,
    humanLabel: 'Prioritizing warm, beautiful lighting',
  },
  {
    name: 'editorial',
    keywords: ['clean', 'minimal', 'editorial', 'magazine', 'sleek', 'modern', 'sharp'],
    portraitDelta: 0.02, lightingDelta: 0.05, compositionDelta: 0.10, sceneDelta: -0.17,
    humanLabel: 'Prioritizing clean composition and sharp lighting',
  },
  {
    name: 'cozy',
    keywords: ['cozy', 'intimate', 'close', 'warm', 'soft', 'home', 'comfort', 'indoor'],
    portraitDelta: 0.05, lightingDelta: 0.10, compositionDelta: -0.05, sceneDelta: -0.10,
    humanLabel: 'Prioritizing warm lighting and genuine expressions',
  },
];

// ─── Detractor Thresholds ────────────────────────────────────
export interface DetractorConfig {
  crowdThresholds: { faces: number; penalty: number }[];
  clutterEdgeDensityThreshold: number;
  clutterMaxPenalty: number;
  photobomberMaxPenalty: number;
  realEstatePerFacePenalty: number;
}

export const DEFAULT_DETRACTOR_CONFIG: DetractorConfig = {
  crowdThresholds: [
    { faces: 2, penalty: 0.1 },
    { faces: 3, penalty: 0.3 },
    { faces: 5, penalty: 0.6 },
    { faces: 8, penalty: 1.0 },
  ],
  clutterEdgeDensityThreshold: 0.25,
  clutterMaxPenalty: 0.5,
  photobomberMaxPenalty: 1.5,
  realEstatePerFacePenalty: 0.4,
};

// ─── Edit Types ──────────────────────────────────────────────
export interface EditRecipe {
  dimension: DimensionKey;
  editName: string;
  intensity: number; // 0.0–1.0
  description: string;
}

export interface EditResult {
  beforeScore: number;
  afterScore: number;
  editsApplied: EditRecipe[];
  editedImageUri: string;
  summary: string;
}

// ─── Analytics Events ────────────────────────────────────────
export type AnalyticsEvent =
  | { type: 'session_started'; mode: ScoringModeId; photoCount: number }
  | { type: 'session_completed'; mode: ScoringModeId; avgScore: number; duration: number }
  | { type: 'score_card_viewed'; photoId: string; score: number }
  | { type: 'edit_applied'; photoId: string; intensity: EditIntensity }
  | { type: 'download'; photoId: string; tier: SubscriptionTier }
  | { type: 'paywall_shown'; trigger: string; mode: ScoringModeId }
  | { type: 'subscription_started'; tier: SubscriptionTier }
  | { type: 'subscription_cancelled'; tier: SubscriptionTier };

// ─── Diagnostic Card ────────────────────────────────────────
export interface DiagnosticBlockingFactor {
  dimension: DimensionKey;
  impactPoints: number;
  description: string;
}

export interface DiagnosticSuggestion {
  action: string;
  specificity: 'general' | 'specific';
  estimatedScoreDelta: number;
}

export interface DiagnosticCardResult {
  overallScore: number;
  dimensionScores: Record<DimensionKey, number | null>;
  blockingFactors: DiagnosticBlockingFactor[];
  suggestions: DiagnosticSuggestion[];
  estimatedNewScore: number;
  summary: string;
}

// ─── Novel Dimensions (v3) ──────────────────────────────────
export * from './novel-dimensions';
